/**
 * Folder List View Component
 *
 * Displays folders as clickable cards with file counts
 * Used for learning/test categories
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { FolderNode } from '../../models/FileStorage';
import { FileStorageService } from '../../services/FileStorageService';
import { ImagePreviewDialog } from './ImagePreviewDialog';

// Shared utility — check if file is an image by extension
export const isImageFile = (filename: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return imageExtensions.includes(ext);
};

interface FolderListViewProps {
  folders: FolderNode[];
  onFolderClick: (folder: FolderNode) => void;
  onFolderSelect?: (folder: FolderNode) => void;
  selectedFolder?: FolderNode | null;
  projectId: string;
  category: string;
  onDownload?: (filename: string) => void;
  onDeleteFolder?: (folderPath: string) => void;
  onDeleteFile?: (filename: string) => void;
  onDedupFilter?: (folderPath: string, folderName: string) => void;
  onDownloadFolder?: (folderPath: string, folderName: string) => void;
}

/**
 * FolderListView Component
 *
 * Grid-based folder visualization with:
 * - Folder icon
 * - Folder name
 * - File count badge
 * - Click to navigate
 */
export const FolderListView: React.FC<FolderListViewProps> = ({
  folders,
  onFolderClick,
  onFolderSelect,
  selectedFolder,
  projectId,
  category,
  onDownload,
  onDeleteFolder,
  onDeleteFile,
  onDedupFilter,
  onDownloadFolder,
}) => {
  const [previewImage, setPreviewImage] = useState<FolderNode | null>(null);

  // Handle single click - select folder, preview image
  const handleItemClick = (item: FolderNode) => {
    if (item.isFolder) {
      if (onFolderSelect) {
        onFolderSelect(item);
      }
    } else if (isImageFile(item.name)) {
      setPreviewImage(item);
    } else {
      onFolderClick(item);
    }
  };

  // Handle double click - navigate into folder
  const handleItemDoubleClick = (item: FolderNode) => {
    if (item.isFolder) {
      onFolderClick(item);
    }
  };

  // Handle keyboard Enter - navigate into selected folder
  const handleKeyDown = (event: React.KeyboardEvent, item: FolderNode) => {
    if (event.key === 'Enter' && item.isFolder && selectedFolder?.path === item.path) {
      onFolderClick(item);
    }
  };

  const handleDeleteFolder = (event: React.MouseEvent, item: FolderNode) => {
    event.stopPropagation(); // Prevent card click
    if (onDeleteFolder && item.isFolder) {
      onDeleteFolder(item.path);
    }
  };

  const handleDeleteFile = (event: React.MouseEvent, item: FolderNode) => {
    event.stopPropagation(); // Prevent card click
    if (onDeleteFile && !item.isFolder) {
      onDeleteFile(item.path);
    }
  };

  const handleDedupFilter = (event: React.MouseEvent, item: FolderNode) => {
    event.stopPropagation();
    if (onDedupFilter && item.isFolder) {
      onDedupFilter(item.path, item.name);
    }
  };

  const handleDownloadFolder = (event: React.MouseEvent, item: FolderNode) => {
    event.stopPropagation();
    if (onDownloadFolder && item.isFolder) {
      onDownloadFolder(item.path, item.name);
    }
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  const handleDownloadFromPreview = (filename: string) => {
    if (onDownload) {
      onDownload(filename);
    }
  };

  // Empty state
  if (!folders || folders.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed #ccc', borderRadius: 1 }}>
        <FolderIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          폴더가 없습니다. 파일을 업로드하면 자동으로 폴더가 생성됩니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
        폴더 목록 ({folders?.length || 0}개)
      </Typography>

      <Box
        sx={{
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
        {folders.map((item) => {
          const isSelected = selectedFolder?.path === item.path;
          const isImage = !item.isFolder && isImageFile(item.name);
          const Icon = item.isFolder ? FolderIcon : isImage ? ImageIcon : FileIcon;

          return (
            <Card
              key={item.path}
              elevation={isSelected ? 4 : 1}
              sx={{
                border: isSelected ? '2px solid primary.main' : '1px solid #e0e0e0',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                '&:hover': {
                  elevation: 3,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Folder action buttons */}
              {item.isFolder && onDownloadFolder && (
                <Tooltip title="폴더 다운로드">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => handleDownloadFolder(e, item)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: onDedupFilter ? 72 : 40,
                      zIndex: 1,
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'info.light',
                        color: 'white',
                      },
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {item.isFolder && onDedupFilter && (
                <Tooltip title="중복 이미지 필터">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => handleDedupFilter(e, item)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 40,
                      zIndex: 1,
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'white',
                      },
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {item.isFolder && onDeleteFolder && (
                <Tooltip title="폴더 삭제">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteFolder(e, item)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!item.isFolder && onDeleteFile && (
                <Tooltip title="파일 삭제">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteFile(e, item)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <CardActionArea
                onClick={() => handleItemClick(item)}
                onDoubleClick={() => handleItemDoubleClick(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Icon
                      sx={{
                        fontSize: 40,
                        color: isSelected
                          ? 'primary.main'
                          : item.isFolder
                          ? 'action.active'
                          : isImage
                          ? 'success.main'
                          : 'info.main',
                        mr: 1,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        noWrap
                        sx={{
                          fontWeight: isSelected ? 'bold' : 'normal',
                          color: isSelected ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {item.isFolder && item.count !== undefined && (
                      <Chip
                        label={`${item.count}개 항목`}
                        size="small"
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                      />
                    )}
                    {!item.isFolder && item.size !== undefined && (
                      <Chip
                        label={FileStorageService.formatFileSize(item.size)}
                        size="small"
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                      />
                    )}
                    {!item.isFolder && isImage && (
                      <Chip
                        label="이미지"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {!item.isFolder && item.uploadDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, width: '100%' }}>
                        {FileStorageService.formatDate(item.uploadDate)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
        </Box>
      </Box>

      {/* Image Preview Dialog */}
      <ImagePreviewDialog
        open={!!previewImage}
        imageFile={previewImage}
        projectId={projectId}
        category={category}
        allFiles={folders}
        onClose={handleClosePreview}
        onDownload={handleDownloadFromPreview}
      />
    </Box>
  );
};
